import React from 'react';
import { useTranslation } from 'react-i18next';
import { SpecialOffer } from '../../../../../interfaces/special-offer';
import { jsonTradTs } from '../../../../../convert-ts';
import { Box, ThemeProvider, Typography } from '@mui/material';
import { backYouTheme } from '../../../../../theme';
import { styled as muiStyled } from '@mui/material/styles';
import { titleCase } from '../../../../../utils';
import dayjs from 'dayjs';

type Props = {
	className?: string,
	specialOffer?: SpecialOffer;
	lang?: string;
}

export default function SpecialOffersPreview({ specialOffer, lang, ...props }: Props) {
	const { t: gctrad } = useTranslation('translation', { lng: (lang ?? ENV.lang) });

	const renderDateRange = (dateStart: string, dateEnd: string): string => {
		if (dateStart !== '' && dateEnd !== '') {
			const start = dayjs(String(dateStart));
			const end = dayjs(String(dateEnd));
			if (!isNaN(start?.year()) && !isNaN(end?.year())) {
				const currentYear = (start.year() === end.year()) ? start.year() : null;
				return titleCase(`${gctrad('du').ucFirst()} ${start.format(currentYear ? 'DD MMMM' : 'DD MMM YYYY')} ${gctrad('au').ucFirst()} ${end.format('DD MMMM YYYY')}`);
			}
		}
		else if (dateStart !== '') {
			const start = dayjs(String(dateStart));
			if (!isNaN(start?.year())) {
				return titleCase(`${gctrad('A partir du')} ${start.format('DD MMMM YYYY')}`);
			}
		}
		return '';
	}

	return (
		<ThemeProvider theme={backYouTheme}>
			{ specialOffer && (
				<div className={`row ${props.className}`}>
					<ImageContainer className="col-6">
						{(specialOffer?.files?.[0] ?? null) ? (
							<Image
								className="mr-2"
								url={`/${specialOffer.files[0].path}${(specialOffer.files[0]?.name) ? '/' + specialOffer.files[0].name : '' }`}
							/>
						) : (
							<div className="d-flex justify-content-center align-items-center text-gc-dark-blue h-100">
								<i className="fa-light fa-image-slash" style={{ fontSize : '5rem' }}></i>
							</div>
						)}
					</ImageContainer>
					<DescriptionContainer className="col-6">
						<div className="text-gc-dark-blue mx-2">
							<h2 className="h4 font-weight-bold">
								<span>{jsonTradTs(specialOffer.title, lang)}</span>
							</h2>
							{ specialOffer.stayingDateStart && (
								<Typography className="pb-2">
									<strong>{gctrad('Staying dates')} : </strong>
									{renderDateRange(specialOffer.stayingDateStart, specialOffer.stayingDateEnd)}
								</Typography>
							)}
							{ specialOffer.bookingDateStart && (
								<Typography className="pb-2">
									<strong>{gctrad('Booking dates')} : </strong>
									{renderDateRange(specialOffer.bookingDateStart, specialOffer.bookingDateEnd)}
								</Typography>
							)}
							<Typography
								sx={{
									'& p': {
										marginBottom: 0
									}
								}} 
								fontSize={'90%'} 
								dangerouslySetInnerHTML={{ __html: `${jsonTradTs(specialOffer.description, lang)}` }} 
							/>
						</div>
					</DescriptionContainer>
				</div>
			)}
		</ThemeProvider>
	);
}

const ImageContainer = muiStyled(Box)({
    maxHeight: '16vw',
	overflow: 'hidden'
});

const Image = muiStyled(Box)(({ url }:{ url: string }) => ({
	backgroundImage: `url(${url})`,
	backgroundRepeat: 'no-repeat',
	backgroundPosition: 'center',
	backgroundSize: 'cover',
	width: '100%',
	height: '100%'
}));

const DescriptionContainer = muiStyled(Box)({
    maxHeight: '16vw',
	overflowY: 'auto',
	'::-webkit-scrollbar': {
		backgroundColor: 'transparent',
		width: '5px',
		padding: '5px'
	},
	'::-webkit-scrollbar-track': {
		'backgroundColor': 'transparent',
		'padding': '5px'
	},
	'::-webkit-scrollbar-thumb': {
		width: '5px',
		background: 'rgba(0, 0, 0, 0.2)',
		borderRadius: '10px'
	}
});